/**
 * HOV coupon engine — mini cart, cart page and coupons page.
 *
 * Replaces the old client-side simulation in snippets/minicart.liquid, which compared the typed
 * code against one theme setting, computed the discount in JS and kept it in sessionStorage.
 *
 * Now the cart itself is the source of truth. POST /cart/update.js {discount} applies the code
 * server-side (Shopify validates it, its rules and customer eligibility), and checkout inherits
 * it from the cart, so no ?discount= on the checkout link and no sessionStorage anywhere.
 *
 * Catalog comes from #hov-coupon-catalog, already filtered server-side by
 * snippets/hov-coupon-catalog.liquid — whatever is in it, this visitor is allowed to see.
 */
(function () {
  'use strict';

  var CART_UPDATE = '/cart/update.js';
  var CART_GET = '/cart.js';

  // Shopify collapses every rejection reason — bad code, expired, usage exhausted, wrong customer
  // — into a single applicable:false, deliberately, to stop brute-force enumeration. So there is
  // exactly one honest message for all of them. Don't guess at a specific reason.
  var ERR_GENERIC = 'This code can’t be applied to your cart.';
  var ERR_NETWORK = 'Couldn’t reach the store — please try again.';
  var ERR_EMPTY = 'Your cart is empty — add an item first.';
  var ERR_TOO_MANY = 'Too many attempts. Please wait a moment.';
  var MSG_SHIPPING = 'Shipping discounts are shown at checkout after adding an address.';

  var MAX_FAILURES = 10;
  var COOLDOWN_MS = 30000;

  // How many cards the cart drawer shows. The catalog is sorted by `sort` below, so this is
  // "the merchant's top N", and "View all coupons ›" carries the rest. The catalog itself is
  // capped at 50 entries server-side, which is far too many for a 400px drawer.
  var DRAWER_CARD_LIMIT = 4;

  var failures = 0;
  var cooldownUntil = 0;
  var inFlight = false;
  var lastCart = null;

  // ── Catalog ──────────────────────────────────────────────────────────────────────────────
  var catalog = (function () {
    var el = document.getElementById('hov-coupon-catalog');
    if (!el) return [];
    try {
      var list = JSON.parse(el.textContent || '[]');
      if (!Array.isArray(list)) return [];
      return list.sort(function (a, b) { return (a.sort || 0) - (b.sort || 0); });
    } catch (e) {
      return [];
    }
  })();

  function findCoupon(code) {
    var want = String(code || '').trim().toUpperCase();
    for (var i = 0; i < catalog.length; i++) {
      if (catalog[i].code === want) return catalog[i];
    }
    return null;
  }

  // ── Money ────────────────────────────────────────────────────────────────────────────────
  // Matches the rest of the cart pipeline, which formats through Shopify.formatMoney when it is
  // available and falls back to the theme's ₹ literal otherwise.
  function money(cents) {
    var v = Math.round(cents || 0);
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      var fmt = (window.ajaxCartConfig && ajaxCartConfig.moneyFormat) || '₹{{amount}}';
      try { return Shopify.formatMoney(v, fmt); } catch (e) { /* fall through */ }
    }
    return '₹' + (v / 100).toFixed(2);
  }

  // ── Cart reads ───────────────────────────────────────────────────────────────────────────
  // discount_codes is the ONLY complete list of codes on the cart. Rebuilding the set from
  // cart_level_discount_applications (as Shopify's own Horizon theme does) silently drops
  // free-shipping codes and applicable:false codes, which then get deleted on the next apply
  // because `discount` is replace-the-whole-set.
  function appliedCodes(cart) {
    var out = [];
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && codes[i].code) out.push(codes[i].code);
    }
    return out;
  }

  // Upcased because Shopify echoes back the casing the merchant used when creating the discount,
  // while the catalog is upcased in Liquid — comparing raw would never match. Codes are
  // case-insensitive to Shopify, so normalising is safe.
  function activeCode(cart) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && codes[i].applicable) return String(codes[i].code).toUpperCase();
    }
    return null;
  }

  // The cart before any discount. items_subtotal_price is NOT that: it already has line-level
  // (product) discounts taken off, so a product-level code such as the WELLNESS25 MRP discount
  // (apps/hov-mrp-discount) would read as ₹0 off against it, and its ₹999 minimum would appear to
  // un-meet itself once applied. Checkout's own minimum test uses this pre-discount figure too.
  function preDiscountSubtotal(cart) {
    if (!cart) return 0;
    return typeof cart.original_total_price === 'number'
      ? cart.original_total_price
      : (cart.items_subtotal_price || 0);
  }

  // Everything taken off the cart, product-level and order-level alike, derived from the two
  // figures the modal shows so its Subtotal − Discount = Total always reconciles.
  function cartDiscount(cart) {
    if (!cart) return 0;
    return Math.max(0, preDiscountSubtotal(cart) - (cart.total_price || 0));
  }

  // What hov-cart-rewards.js predicts an MRP-based promo code takes off this cart (same arithmetic
  // as checkout), or null for any other code / when the theme marks the code as not MRP-based.
  function mrpPreview(code, cart) {
    var api = window.hovRewards;
    if (!api || typeof api.couponPreview !== 'function') return null;
    try { return api.couponPreview(code, cart); } catch (e) { return null; }
  }

  function getCart() {
    return fetch(CART_GET, { credentials: 'same-origin' }).then(readCart);
  }

  // Shopify answers 200 for almost everything, so a non-cart body is the one failure that would
  // surface as a raw TypeError downstream. Normalise it into a rejection here.
  function readCart(res) {
    if (!res.ok) throw new Error('cart http ' + res.status);
    return res.json().then(function (data) {
      if (!data || typeof data !== 'object' || !('item_count' in data)) throw new Error('not a cart');
      return data;
    });
  }

  function postDiscounts(codes) {
    return fetch(CART_UPDATE, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    }).then(readCart);
  }

  // ── Rendering ────────────────────────────────────────────────────────────────────────────
  // Surface-agnostic: every cart surface on the page is driven from one computation, matching
  // the querySelectorAll fan-out the old engine established (drawer + /cart page together).
  function each(sel, fn) {
    var nodes = document.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) fn(nodes[i]);
  }

  function render(cart) {
    lastCart = cart;
    var code = activeCode(cart);
    var discount = cartDiscount(cart);
    var active = !!code && discount > 0;

    each('.hov-cart__coupon-preview', function (p) {
      var input = p.querySelector('[data-coupon-input]');
      var pill = p.querySelector('[data-coupon-applied]');
      var pillCode = p.querySelector('[data-coupon-applied-code]');
      var applyBtn = p.querySelector('[data-coupon-apply]');
      if (input) input.hidden = active;
      if (applyBtn) applyBtn.hidden = active;
      if (pill) pill.hidden = !active;
      if (active && pillCode) pillCode.textContent = code;
    });

    each('[data-cart-discount]', function (row) {
      if (active) row.removeAttribute('hidden');
      else row.setAttribute('hidden', 'hidden');
    });
    each('[data-cart-discount-amount]', function (el) { el.textContent = '− ' + money(discount); });

    each('[data-coupon-saved-row]', function (row) { row.hidden = !active; });
    each('[data-coupon-saved-amount]', function (el) { el.textContent = money(discount); });

    // total_price is already post-discount, so there is no overlay to apply and no competing
    // writer — this is what removes the old three-way race on .enj-cartcost.
    each('.enj-cartcost', function (el) { el.textContent = money(cart.total_price); });

    renderList(cart);
    // assets/hov-cart-rewards.js repaints its promo milestone from this cart (discount_codes).
    document.dispatchEvent(new CustomEvent('hov:coupons:rendered', { detail: { cart: cart } }));
  }

  function conditionState(coupon, cart) {
    if (!coupon) return { ok: true, note: '' };
    var min = Math.round((coupon.minSubtotal || 0) * 100);
    var base = preDiscountSubtotal(cart);
    // The MRP code tests its minimum on the cart at MRP (checkout does the same). While a line's
    // MRP is still loading there is nothing reliable to compare, so don't block on a guess.
    var mrp = mrpPreview(coupon.code, cart);
    if (mrp) {
      if (!mrp.known) return { ok: true, note: '' };
      base = mrp.total;
    }
    if (min > 0 && cart && base < min) {
      return {
        ok: false,
        note: 'Add ' + money(min - base) + ' more to use this code.'
      };
    }
    return { ok: true, note: '' };
  }

  function couponRow(coupon, cart, currentCode) {
    var applied = currentCode === coupon.code;
    var state = conditionState(coupon, cart);
    var li = document.createElement('li');
    li.className = 'hov-coupon-row' + (applied ? ' is-applied' : '') + (state.ok ? '' : ' is-blocked');

    var info = document.createElement('div');
    info.className = 'hov-coupon-row__info';

    var codeEl = document.createElement('span');
    codeEl.className = 'hov-coupon-row__code';
    codeEl.textContent = coupon.code;
    info.appendChild(codeEl);

    if (coupon.private) {
      var badge = document.createElement('span');
      badge.className = 'hov-coupon-row__badge';
      badge.textContent = 'For you';
      info.appendChild(badge);
    }

    var head = document.createElement('p');
    head.className = 'hov-coupon-row__heading';
    head.textContent = coupon.heading || '';
    info.appendChild(head);

    if (coupon.subtext) {
      var sub = document.createElement('p');
      sub.className = 'hov-coupon-row__subtext';
      sub.textContent = coupon.subtext;
      info.appendChild(sub);
    }

    if (!state.ok) {
      var note = document.createElement('p');
      note.className = 'hov-coupon-row__note';
      note.textContent = state.note;
      info.appendChild(note);
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hov-coupon-row__btn';
    if (applied) {
      btn.textContent = 'Remove';
      btn.setAttribute('data-coupon-remove', '');
    } else {
      btn.textContent = 'Apply';
      btn.setAttribute('data-coupon-pick', coupon.code);
      btn.disabled = !state.ok;
    }

    li.appendChild(info);
    li.appendChild(btn);
    return li;
  }

  // ── Offers-page card ─────────────────────────────────────────────────────────────────────
  // Richer presentation of the same catalog entry, for sections/hov-coupons-page.liquid.
  var SVG = {
    gift: 'M3 8h18v13H3zM3 12h18M12 8v13M12 8S9.5 3 7 4.5 9 8 12 8zM12 8s2.5-5 5-3.5S15 8 12 8z',
    truck: 'M1 3h15v13H1zM16 8h4l3 3v5h-7z',
    // The two dots are drawn as real r=2.5 arcs, not the Feather "h.01" idiom: at the 19px
    // the drawer badge renders, a 1.6-wide dot is ~1.3px and the glyph reads as a bare slash.
    percent: 'M19 5L5 19M9 6.5a2.5 2.5 0 1 1-5 0a2.5 2.5 0 1 1 5 0M20 17.5a2.5 2.5 0 1 1-5 0a2.5 2.5 0 1 1 5 0'
  };

  function svgIcon(d) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.6');
    // Round caps/joins so the gift/truck strokes meet cleanly at their corners rather than
    // ending in hard mitres at badge size.
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
    return svg;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // Badge sits in a narrow column — 104px on the coupons page, 72px in the drawer — so trailing
  // ".00" is dropped: whole-rupee offers read as "₹1,800", not "₹1800.00". Non-round amounts
  // keep their paise.
  // The badge's two lines are a single phrase split over two rows, and the word order depends on
  // the type: "FLAT / 25% OFF" but "FREE / Shipping". labelFirst says which line leads, so the
  // emphasis (.hov-offer__badge-value) always lands on the number, wherever it sits.
  function badgeFor(coupon) {
    var type = (coupon.type || 'percentage').toLowerCase();
    var v = coupon.value || 0;
    if (type === 'free_shipping') {
      return { kind: 'ship', icon: SVG.truck, value: 'FREE', label: 'Shipping', labelFirst: false };
    }
    if (type === 'fixed_amount') {
      return { kind: 'amt', icon: SVG.gift, value: money(v * 100).replace(/\.00$/, ''), label: 'Flat off', labelFirst: true };
    }
    return { kind: 'pct', icon: SVG.percent, value: v + '% OFF', label: 'Flat', labelFirst: true };
  }

  function couponCard(coupon, cart, currentCode, mode) {
    var applied = currentCode === coupon.code;
    var state = conditionState(coupon, cart);
    var b = badgeFor(coupon);

    var card = el('div', 'hov-offer hov-offer--' + b.kind +
      (mode === 'card' ? ' hov-offer--compact' : '') +
      (applied ? ' is-applied' : '') + (state.ok ? '' : ' is-blocked'));

    var badge = el('div', 'hov-offer__badge');
    badge.appendChild(svgIcon(b.icon));
    var badgeValue = el('span', 'hov-offer__badge-value', b.value);
    var badgeLabel = el('span', 'hov-offer__badge-label', b.label);
    badge.appendChild(b.labelFirst ? badgeLabel : badgeValue);
    badge.appendChild(b.labelFirst ? badgeValue : badgeLabel);
    card.appendChild(badge);

    var main = el('div', 'hov-offer__main');

    var codeRow = el('div', 'hov-offer__code-row');
    // <code>, not <span>: assets/hov-typography.css sets font-family !important on a selector
    // list that includes span, which beats .hov-offer__code's monospace rule on specificity-
    // proof grounds. code/kbd/samp/pre are deliberately absent from that list.
    codeRow.appendChild(el('code', 'hov-offer__code', coupon.code));

    var copy = el('button', 'hov-offer__copy');
    copy.type = 'button';
    copy.setAttribute('data-coupon-copy', coupon.code);
    copy.setAttribute('aria-label', 'Copy code ' + coupon.code);
    copy.appendChild(svgIcon('M9 9h10v10H9zM5 15V5h10'));
    codeRow.appendChild(copy);

    if (coupon.private) codeRow.appendChild(el('span', 'hov-offer__tag', 'For you'));
    main.appendChild(codeRow);

    var desc = [coupon.heading, coupon.subtext].filter(Boolean).join(' — ');
    if (desc) main.appendChild(el('p', 'hov-offer__desc', desc));
    if (!state.ok) main.appendChild(el('p', 'hov-offer__note', state.note));
    if (coupon.endsAt) main.appendChild(el('p', 'hov-offer__meta', 'Valid till ' + coupon.endsAt));

    var actions = el('div', 'hov-offer__actions');
    var act = el('button', 'hov-offer__btn');
    act.type = 'button';
    if (applied) {
      act.textContent = 'Remove';
      act.className += ' hov-offer__btn--ghost';
      act.setAttribute('data-coupon-remove', coupon.code);
    } else {
      // In a cart context applying beats copying — one tap and the discount is on the cart.
      // The small [data-coupon-copy] icon above stays as the secondary affordance.
      act.textContent = 'Apply';
      act.setAttribute('aria-label', 'Apply code ' + coupon.code);
      act.setAttribute('data-coupon-pick', coupon.code);
      act.disabled = !state.ok;
    }
    actions.appendChild(act);
    main.appendChild(actions);

    card.appendChild(main);
    return card;
  }

  // Fills content only — never touches host.hidden. On the /cart page the list starts collapsed
  // behind "More offers" and only [data-coupon-list-toggle] owns that state; the cart drawer and
  // the coupons page start visible. Toggling visibility here would reopen or close it on every sync.
  //
  // data-coupon-list picks the renderer:
  //   ''      compact <li> rows      — /cart page summary card (space-constrained, collapsible)
  //   'card'  .hov-offer--compact    — cart drawer, always visible, capped at DRAWER_CARD_LIMIT
  //   'page'  .hov-offer             — coupons page grid, the full catalog
  function renderList(cart) {
    var current = activeCode(cart);
    var empty = !catalog.length;

    each('[data-coupon-list]', function (host) {
      host.innerHTML = '';
      if (empty) return;
      // A bare attribute reads as '', never null, so the row default is safe.
      var mode = host.getAttribute('data-coupon-list') || 'row';
      var cards = mode === 'page' || mode === 'card';
      var max = mode === 'card' ? Math.min(catalog.length, DRAWER_CARD_LIMIT) : catalog.length;
      var wrap = cards ? host : el('ul', 'hov-coupon-list');
      for (var i = 0; i < max; i++) {
        wrap.appendChild(cards ? couponCard(catalog[i], cart, current, mode)
                               : couponRow(catalog[i], cart, current));
      }
      if (!cards) host.appendChild(wrap);
    });

    // Nothing to reveal: hide the toggle rather than offering an empty panel.
    each('[data-coupon-list-toggle]', function (t) { t.hidden = empty; });
    each('[data-coupons-empty]', function (t) { t.hidden = !empty; });
  }

  // ── Errors ───────────────────────────────────────────────────────────────────────────────
  function showError(msg) {
    each('[data-coupon-error]', function (el) {
      el.textContent = msg;
      el.setAttribute('data-visible', 'true');
    });
  }
  function clearError() {
    each('[data-coupon-error]', function (el) { el.removeAttribute('data-visible'); });
  }

  function setBusy(busy) {
    inFlight = busy;
    each('[data-coupon-apply], [data-coupon-pick], [data-coupon-remove]', function (b) {
      b.disabled = busy;
    });
  }

  // ── Apply / remove ───────────────────────────────────────────────────────────────────────
  function apply(code) {
    var want = String(code || '').trim().toUpperCase();
    if (!want) { showError('Enter a coupon code.'); return; }
    if (inFlight) return;
    if (Date.now() < cooldownUntil) { showError(ERR_TOO_MANY); return; }

    clearError();
    setBusy(true);

    getCart()
      .then(function (cart) {
        if (!cart.item_count) throw { soft: ERR_EMPTY };

        // The minimum-spend check is the only precise error available to us, because Shopify
        // refuses to distinguish its own rejection reasons. Worth owning: catch it before the
        // request so the customer gets a real shortfall instead of the generic message.
        var known = findCoupon(want);
        var state = conditionState(known, cart);
        if (!state.ok) throw { soft: state.note };

        // Same for the MRP code on a cart already at or below its floor: checkout would take
        // nothing off, so say why instead of letting Shopify refuse it with the generic message.
        var mrp = mrpPreview(want, cart);
        if (mrp && mrp.known && mrp.save <= 0) throw { soft: mrp.message };

        var codes = appliedCodes(cart).filter(function (c) { return c !== want; });
        codes.push(want);
        return postDiscounts(codes);
      })
      .then(function (cart) {
        // discount_codes is undocumented (though Shopify's own Horizon theme relies on it). If a
        // store ever answers without it, fall back to the observable effect: did a discount land?
        if (!Array.isArray(cart.discount_codes)) {
          render(cart);
          if (cartDiscount(cart) > 0) { failures = 0; openModal(cart, want); }
          else showError(ERR_GENERIC);
          return;
        }

        var entry = null;
        var list = cart.discount_codes;
        for (var i = 0; i < list.length; i++) {
          if (list[i].code && list[i].code.toUpperCase() === want) entry = list[i];
        }

        if (!entry || !entry.applicable) {
          failures += 1;
          if (failures >= MAX_FAILURES) { cooldownUntil = Date.now() + COOLDOWN_MS; failures = 0; }
          // Drop the rejected code back off the cart so it can't be re-posted later as part of
          // the "current set" and can't reach checkout.
          var why = mrpPreview(want, cart);
          return postDiscounts(appliedCodes(cart).filter(function (c) { return c !== want; }))
            .then(function (clean) { render(clean); showError(why && why.save <= 0 ? why.message : ERR_GENERIC); });
        }

        failures = 0;
        render(cart);

        // A shipping code reports applicable:true but allocates nothing until checkout has an
        // address, so it produces no cart-level discount. Say so, rather than celebrating ₹0.
        if (cartDiscount(cart) <= 0) {
          var zero = mrpPreview(want, cart);
          showError(zero ? zero.message : MSG_SHIPPING);
          return;
        }

        openModal(cart, want);
      })
      .catch(function (err) {
        showError(err && err.soft ? err.soft : ERR_NETWORK);
      })
      .then(function () { setBusy(false); });
  }

  function remove(code) {
    if (inFlight) return;
    clearError();
    setBusy(true);
    getCart()
      .then(function (cart) {
        var keep = appliedCodes(cart);
        if (code) keep = keep.filter(function (c) { return c.toUpperCase() !== String(code).toUpperCase(); });
        else keep = [];
        return postDiscounts(keep);
      })
      .then(render)
      .catch(function () { showError(ERR_NETWORK); })
      .then(function () { setBusy(false); });
  }

  // ── Success modal ────────────────────────────────────────────────────────────────────────
  // Built from the applied coupon's own catalog entry, so each coupon shows its own conditions
  // rather than the one hardcoded message the old modal had.
  function openModal(cart, code) {
    var modal = document.getElementById('hov-coupon-modal');
    if (!modal) return;
    var coupon = findCoupon(code) || {};
    var discount = cartDiscount(cart);

    // querySelectorAll, not querySelector: the code appears twice in the modal (chip + discount row).
    var set = function (sel, val) {
      var nodes = modal.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) nodes[i].textContent = val;
    };
    set('[data-coupon-modal-code]', code);
    set('[data-coupon-modal-heading]', coupon.heading || 'Discount applied');
    set('[data-coupon-saved-amount]', money(discount));
    set('[data-coupon-subtotal]', money(preDiscountSubtotal(cart)));
    set('[data-coupon-item-count]', cart.item_count);
    set('[data-coupon-discount]', '− ' + money(discount));
    set('[data-coupon-total]', money(cart.total_price));

    var termsHost = modal.querySelector('[data-coupon-modal-terms]');
    if (termsHost) {
      termsHost.innerHTML = '';
      var lines = String(coupon.terms || '').split('\n');
      var any = false;
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i].trim();
        if (!line) continue;
        var li = document.createElement('li');
        li.textContent = line;
        termsHost.appendChild(li);
        any = true;
      }
      termsHost.hidden = !any;
    }

    var shipEl = modal.querySelector('[data-coupon-shipping]');
    if (shipEl) {
      var bar = document.querySelector('.hov-cart-shipping');
      var threshold = bar ? parseFloat(bar.getAttribute('data-threshold')) : NaN;
      var free = !isNaN(threshold) && cart.total_price >= threshold * 100;
      shipEl.textContent = free ? 'FREE' : 'Calculated at checkout';
      shipEl.classList.toggle('hov-coupon-modal__shipping-free', free);
    }

    modal.dataset.state = 'visible';
    setTimeout(confetti, 300);
  }

  function closeModal() {
    var modal = document.getElementById('hov-coupon-modal');
    if (modal) modal.dataset.state = 'hidden';
  }

  // Runtime-generated confetti burst — no library, no static DOM, self-removing.
  // Ported unchanged from the previous inline engine.
  function confetti() {
    var wrap = document.getElementById('hov-confetti-wrap');
    if (!wrap) return;
    wrap.innerHTML = '';
    var colors = ['#2D5A27', '#6B9E5F', '#E8B92E', '#F5D77A', '#C0392B'];
    var count = 36;
    for (var i = 0; i < count; i++) {
      var el = document.createElement('i');
      var size = 5 + Math.random() * 7;
      var isCircle = Math.random() > 0.5;
      var angle = (2 * Math.PI * i) / count + (Math.random() - 0.5) * 0.5;
      var dist = 90 + Math.random() * 90;
      var dx = Math.cos(angle) * dist;
      var dy = Math.sin(angle) * dist - 40;
      var rot = (Math.random() - 0.5) * 720;
      var dur = 700 + Math.random() * 400;
      var delay = Math.random() * 180;
      el.style.cssText = 'position:absolute;top:50%;left:50%;width:' + size + 'px;height:' + size +
        'px;background:' + colors[i % colors.length] + ';border-radius:' + (isCircle ? '50%' : '2px') +
        ';transform:translate(-50%,-50%);opacity:1;pointer-events:none;';
      wrap.appendChild(el);
      (function (node, _dx, _dy, _rot, _dur, _delay) {
        setTimeout(function () {
          node.style.transition = 'transform ' + _dur + 'ms cubic-bezier(0.25,0.46,0.45,0.94), opacity ' +
            (_dur * 0.7) + 'ms ease ' + (_dur * 0.4) + 'ms';
          node.style.transform = 'translate(calc(-50% + ' + _dx + 'px), calc(-50% + ' + _dy + 'px)) rotate(' +
            _rot + 'deg) scale(' + (0.4 + Math.random() * 0.6) + ')';
          node.style.opacity = '0';
        }, _delay);
        setTimeout(function () { node.remove(); }, _dur + _delay + 120);
      })(el, dx, dy, rot, dur, delay);
    }
  }

  // clipboard.writeText needs a secure context and can be blocked outright, so fall back to a
  // throwaway textarea + execCommand rather than leaving the button silently dead.
  function copyCode(btn) {
    var code = btn.getAttribute('data-coupon-copy') || '';
    var done = function () {
      btn.classList.add('is-copied');
      setTimeout(function () { btn.classList.remove('is-copied'); }, 1200);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, function () { legacyCopy(code, done); });
    } else {
      legacyCopy(code, done);
    }
  }

  function legacyCopy(text, done) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:absolute;left:-9999px;';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
      done();
    } catch (e) { /* clipboard unavailable; the code is visible on screen anyway */ }
  }

  // ── Wiring ───────────────────────────────────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;

    var copy = e.target.closest('[data-coupon-copy]');
    if (copy) { copyCode(copy); return; }

    var pick = e.target.closest('[data-coupon-pick]');
    if (pick) { apply(pick.getAttribute('data-coupon-pick')); return; }

    if (e.target.closest('[data-coupon-apply]')) {
      var preview = e.target.closest('.hov-cart__coupon-preview') ||
        document.querySelector('.hov-cart__coupon-preview');
      var input = preview && preview.querySelector('[data-coupon-input]');
      apply(input ? input.value : '');
      return;
    }

    var rm = e.target.closest('[data-coupon-remove]');
    if (rm) { remove(rm.getAttribute('data-coupon-remove') || null); return; }

    var toggle = e.target.closest('[data-coupon-list-toggle]');
    if (toggle) {
      // Scoped to its own widget. This used to read the FIRST [data-coupon-list] in the document
      // and then write `hidden` to ALL of them. snippets/minicart.liquid is included before
      // <main>, so on /cart the drawer's host was always the one read — and now that the drawer's
      // host is never hidden, the old code would conclude "already open", hide every host, and
      // wipe the drawer's cards without ever opening the /cart list.
      var scope = toggle.closest('.hov-cart__coupon-preview');
      var host = scope && scope.querySelector('[data-coupon-list]');
      if (!host) return;
      var openNow = host.hasAttribute('hidden');
      host.hidden = !openNow;
      toggle.setAttribute('aria-expanded', String(openNow));
      return;
    }

    if (e.target.closest('[data-coupon-modal-close]')) closeModal();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var modal = document.getElementById('hov-coupon-modal');
    if (modal && modal.dataset.state === 'visible') closeModal();
  });

  function sync() { getCart().then(render).catch(function () {}); }

  sync();
  // The theme's established bus: ajax-cart.js and page-cart.liquid both fire this after any
  // mutation, including the /cart section re-render.
  //
  // buildCart() empties .enj-minicart-ajax and rebuilds it from #CartTemplate BEFORE dispatching,
  // so the coupon list comes back empty. sync() only refills it after a /cart.js round-trip —
  // invisible while the list was hidden, a visible hole now the drawer cards are always on.
  // Repaint from the cart we already have, then let sync() correct it.
  //
  // renderList only, deliberately: a full render() would rewrite .enj-cartcost with stale money
  // that ajax-cart.js has already written correctly.
  document.addEventListener('cart:updated', function (e) {
    // ajax-cart.js sends the payload it just rendered from, which saves the /cart.js round trip.
    var cart = e.detail && e.detail.cart;
    if (cart && typeof cart === 'object' && 'item_count' in cart && cart.items) {
      render(cart);
      return;
    }
    if (lastCart) renderList(lastCart);
    sync();
  });

  window.hovCoupons = { apply: apply, remove: remove, sync: sync };
})();
